import {useState, useEffect, useMemo} from "react";
import {useHistory, useParams} from "react-router-dom";
import {loadLocalAssets} from "utils/loader";
import {FormCard} from "components/Form";
import {DashboardLayout} from "components/Layout";
import {CircleLoader} from "components/Loader";
import {SubmitButton, ButtonAddTable} from "components/Button";
import {BasicTable} from "components/Table";
import {ModalContainer, ModalConfirmation} from "components/Modal";
import {Tab, Tabs, TabList, TabPanel} from "react-tabs";
import "react-tabs/style/react-tabs.css";
import {useDetailStaff} from "hooks/SuperAdmin/Staff";
import {useStaffApiKeys} from "hooks/AdminHospital/Staff";
import {useGenerateApiKey, useDeleteApiKey} from "hooks/SuperAdmin/ApiKey";
import dayjs from "dayjs";
import {useToast} from "hooks/useToast";
import {isMobile} from "react-device-detect";

function maskKey(key) {
	if (!key) return "";
	const start = key.slice(0, 6);
	const end = key.slice(-4);

	return `${start}****************${end}`;
}

function StaffDetail() {
	const history = useHistory();
	const {id} = useParams();
	const [detail, setDetail] = useState({});
	const {data: detailStaff, isLoading: loadingFetcing} = useDetailStaff(id);

	useEffect(() => {
		if (detailStaff?.success) {
			setDetail({...detailStaff?.data?.staffDetail, ...detailStaff?.data?.userDetail});
		}
	}, [detailStaff]);

	if (loadingFetcing) {
		return (
			<FormCard backTitle="Daftar List Staff" title="Detail Data Staff">
				<CircleLoader />
			</FormCard>
		);
	}

	return (
		<FormCard
			backTitle="Daftar List Staff"
			title={`Detail Data ${detail?.firstName} ${detail?.lastName}`}
			onBackButtonClick={() => history.push("/hospital/staff")}>
			<div class="row gx-2 mb-5">
				<div class="col-sm-12 col-lg-6">
					<div class="setting-item">
						<label class="setting-label ">Foto Profil</label>
						<div class="change-img mb-4">
							<div class="for-img-change">
								<div class="bx-img-change">
									<img
										alt="Profile"
										class="img-fluid"
										src={
											detail?.profilePhoto
												? detail?.profilePhoto
												: loadLocalAssets("img/user-default.jpg")
										}
									/>
								</div>
							</div>
						</div>
					</div>
					<div class="setting-item">
						<label class="setting-label ">Title</label>
						<p className="text-bold text-grey">{detail?.title}</p>
					</div>
					<div class="setting-item">
						<label class="setting-label ">Nama Depan & Belakang</label>
						<p className="text-bold text-grey">{detail?.fullName}</p>
					</div>
					<div class="setting-item">
						<label class="setting-label ">Nama Panggilan</label>
						<p className="text-bold text-grey">{detail?.nickname}</p>
					</div>
					<div class="setting-item">
						<label class="setting-label ">Email</label>
						<p className="text-bold text-grey">{detail?.email}</p>
					</div>
					<div class="setting-item">
						<label class="setting-label ">Nomor Telepon</label>
						<p className="text-bold text-grey">{detail?.phoneNumber}</p>
					</div>
					<div class="setting-item">
						<label class="setting-label ">Usia</label>
						<p className="text-bold text-grey">{detail?.dobText}</p>
					</div>
				</div>

				<div class="col-sm-12 col-lg-6">
					<div class="setting-item">
						<label class="setting-label ">Status Karyawan</label>
						<p className="text-bold text-grey">{detail?.employeeStatusText}</p>
					</div>
					<div class="setting-item">
						<label class="setting-label ">NIP</label>
						<p className="text-bold text-grey">{detail?.nip}</p>
					</div>
					<div class="setting-item">
						<label class="setting-label ">No STR</label>
						<p className="text-bold text-grey">{detail?.noStr}</p>
					</div>
				</div>
			</div>

			<div class="text-center my-3">
				<SubmitButton
					className="btn btn-orange d-inline-block mw-250"
					text="Ubah Data Ini"
					onClick={() => history.push(`/hospital/staff/edit/${id}`)}
				/>
			</div>
		</FormCard>
	);
}

export function StaffDetailPage() {
	const [tab, setTab] = useState(0);
	const {id} = useParams();
	const {showToast} = useToast();

	// API Keys
	const {data: apiKeysRes, isLoading: apiKeysLoading, refetch: refetchApiKeys} = useStaffApiKeys(id);
	const apiKeys = apiKeysRes?.data || [];

	// Create / Delete API Key
	const [showCreate, setShowCreate] = useState(false);
	const [label, setLabel] = useState("");
	const [showDelete, setShowDelete] = useState(false);
	const [deleteData, setDeleteData] = useState({id: null, label: ""});
	const {mutate: createKey, isLoading: isCreating} = useGenerateApiKey();
	const {mutate: deleteKey, isLoading: isDeleting} = useDeleteApiKey();

	const handleCreate = () => {
		if (!label) return;
		createKey(
			{staffId: id, label},
			{
				onSuccess: () => {
					showToast("success", "API Key berhasil dibuat", 3000);
					setShowCreate(false);
					setLabel("");
					refetchApiKeys();
				},
			},
		);
	};

	const handleDelete = () => {
		if (!deleteData?.id) return;
		deleteKey(
			{staffId: id, apiKeyId: deleteData.id},
			{
				onSuccess: () => {
					showToast("success", `API Key ${deleteData.label} dihapus`, 3000);
					setShowDelete(false);
					setDeleteData({id: null, label: ""});
					refetchApiKeys();
				},
			},
		);
	};

	const apiKeyColumns = useMemo(
		() => [
			{Header: "No", accessor: "no", width: 60},
			{Header: "Label", accessor: "label"},
			{Header: "API Key", accessor: "apiKey", Cell: ({row}) => maskKey(row.original.apiKey)},
			{Header: "Dibuat", accessor: "createdAt", Cell: ({value}) => dayjs(value).format("YYYY-MM-DD HH:mm")},
			{
				Header: "Aksi",
				accessor: "id",
				Cell: ({row}) => (
					<div className="d-flex align-items-center">
						<a
							class="text-orange wait-pay"
							data-placement="top"
							data-toggle="tooltip"
							style={{cursor: "pointer"}}
							title="Salin API Key"
							onClick={() => navigator.clipboard && navigator.clipboard.writeText(row.original.apiKey)}>
							<span class="icon-ico-link ft-18" />
						</a>
						<a
							class={"text-orange wait-pay ms-2"}
							data-placement="top"
							data-toggle="tooltip"
							style={{cursor: "pointer"}}
							title="Hapus API Key"
							onClick={() => {
								setDeleteData({id: row.original.id, label: row.original.label});
								setShowDelete(true);
							}}>
							<span class="icon-ico-delete ft-18" />
						</a>
					</div>
				),
			},
		],
		[],
	);

	const apiKeyRows = (apiKeys || []).map((k, idx) => ({...k, no: idx + 1}));

	return (
		<DashboardLayout>
			<Tabs className="mb-0" selectedIndex={tab} onSelect={index => setTab(index)}>
				<div className="" style={isMobile ? {"overflow-x": "auto", "white-space": "nowrap"} : {}}>
					<TabList className="border-0">
						<Tab>Detail Staff</Tab>
						<Tab>API Key</Tab>
					</TabList>
				</div>

				<div className="box-white setting-content h-100 no-border p24">
					<TabPanel>
						<StaffDetail />
					</TabPanel>
					<TabPanel>
						<div className="d-flex justify-content-end mb-3">
							<ButtonAddTable text="Tambah" onClick={() => setShowCreate(true)} />
						</div>
						<BasicTable
							columns={apiKeyColumns}
							data={apiKeyRows}
							hidePagination={true}
							loading={apiKeysLoading}
							striped={true}
							title="Daftar API Key"
						/>

						{/* Create Modal */}
						<ModalContainer visible={showCreate}>
							<div className="box-popup w550">
								<div className="d-flex justify-content-between align-items-center mb-4">
									<div className="text-bold text-dark">Tambah API Key</div>
									<div className="for-close">
										<button
											className="btn btn-close"
											disabled={isCreating}
											type="button"
											onClick={() => setShowCreate(false)}
										/>
									</div>
								</div>

								<div className="popup-content">
									<div className="mb-3">
										<label className="setting-label">Label API Key</label>
										<input
											className="form-control"
											placeholder="Contoh: Integrasi POS"
											value={label}
											onChange={e => setLabel(e.target.value)}
										/>
									</div>
									<div className="d-flex justify-content-end">
										<button
											className="btn btn-orange"
											disabled={isCreating || !label}
											onClick={handleCreate}>
											{isCreating ? "Menyimpan..." : "Simpan"}
										</button>
									</div>
								</div>
							</div>
						</ModalContainer>

						{/* Delete Confirmation */}
						<ModalConfirmation
							approveLabel="Ya"
							description={`Apakah Anda yakin ingin menghapus API Key "${deleteData.label}"?`}
							loading={isDeleting}
							rejectLabel="Batal"
							visible={showDelete}
							onApprove={handleDelete}
							onClose={() => setShowDelete(false)}
							onReject={() => setShowDelete(false)}
						/>
					</TabPanel>
				</div>
			</Tabs>
		</DashboardLayout>
	);
}
